import type { CashAccountBalance, CashCount } from '../api/cash';
import { formatAmount } from '../format/money';
import { lastCount, topUpMove } from './board';
import { outcomeOf } from './count';
import type { TransferDraft } from './transfer';

/**
 * Close the day — the Money board's card that puts the evening in order (owner, 2026-10-26,
 * canvas "Cash & expense flow"): count the till, deposit the rest, top up petty cash, print the day
 * book. Web and phone alike.
 *
 * <p>
 * <strong>It adds no write of its own.</strong> Every step opens a page that already exists — the
 * count, Move money, the day book — filled in from the step before, so there is still one way to do
 * each thing. A step is done when the books say so: the till counted today, nothing above its float,
 * petty cash back at its imprest. Only the day book is not on the books; whether it was printed is
 * the browser's or phone's own note, passed in.
 * </p>
 *
 * <p>
 * <strong>A step that cannot apply is left out</strong> — no till, nothing to count; no bank, nothing
 * to deposit into; no imprest set, nothing to top up — rather than shown and greyed.
 * </p>
 */

export type CloseStepId = 'count' | 'deposit' | 'topUp' | 'dayBook';

/** The first step not yet done is `next`; the rest wait. */
export type CloseStepState = 'done' | 'next' | 'later';

export interface CloseStep {
  id: CloseStepId;
  title: string;
  line: string;
  /** The button's word. */
  action: string;
  state: CloseStepState;
  /** The account a count opens on. */
  accountId: number | null;
  /** Move money filled in, for the deposit and the top-up. */
  move: Partial<TransferDraft> | null;
}

/** The till the evening closes: the branch's default, else its first one still taking money. */
function closingTill(accounts: readonly CashAccountBalance[]): CashAccountBalance | undefined {
  const tills = accounts.filter((a) => a.kind === 'Till' && a.isActive);

  return tills.find((a) => a.isDefault) ?? tills[0];
}

function round(value: number): number {
  return Math.round(value * 100) / 100;
}

export function closeTheDay(input: {
  accounts: readonly CashAccountBalance[] | undefined;
  counts: readonly CashCount[] | undefined;
  today: string;
  dayBookPrinted: boolean;
  currency?: string;
}): CloseStep[] {
  const { counts, today, dayBookPrinted, currency = '' } = input;
  const accounts = input.accounts ?? [];
  const money = (value: number) => `${currency}${formatAmount(value)}`;

  const till = closingTill(accounts);
  const bank = accounts.find((a) => a.kind === 'Bank' && a.isActive && a.isDefault);
  const petty = accounts.find(
    (a) => a.kind === 'PettyCash' && a.isActive && a.floatToKeep != null && a.floatToKeep > 0,
  );

  const pettyShort = petty ? round((petty.floatToKeep ?? 0) - petty.closing) : 0;
  const steps: Omit<CloseStep, 'state'>[] = [];
  const done = new Set<CloseStepId>();

  if (till) {
    const counted = lastCount(counts, till.id);
    const countedToday = counted?.countedOn.slice(0, 10) === today;

    if (countedToday) {
      done.add('count');
    }

    const outcome = counted ? outcomeOf(counted.variance) : 'agrees';

    steps.push({
      id: 'count',
      title: 'Count the till',
      line:
        countedToday && counted
          ? outcome === 'agrees'
            ? 'Counted today · agreed'
            : `Counted today · ${money(Math.abs(counted.variance))} ${outcome}`
          : `The book says ${money(till.closing)}`,
      action: 'Count',
      accountId: till.id,
      move: null,
    });
  }

  if (till && bank) {
    const float = till.floatToKeep ?? 0;
    // A top-up still to come is drawn from the default till, so the deposit leaves room for it.
    const forPetty = pettyShort > 0 && till.isDefault ? pettyShort : 0;
    const rest = round(till.closing - float - forPetty);

    if (rest <= 0) {
      done.add('deposit');
    }

    const keeping = [
      float > 0 ? `${money(float)} float` : null,
      forPetty > 0 ? `${money(forPetty)} for petty cash` : null,
    ].filter(Boolean);

    steps.push({
      id: 'deposit',
      title: 'Deposit the rest',
      line:
        rest > 0
          ? `${keeping.length > 0 ? `Keep ${keeping.join(' and ')} · ` : ''}${money(rest)} to ${bank.name}`
          : float > 0
            ? `Nothing above the ${money(float)} float`
            : 'Nothing in the till to bank',
      action: 'Move',
      accountId: till.id,
      move:
        rest > 0
          ? {
              fromId: till.id,
              toId: bank.id,
              toBranchId: null,
              amount: String(rest),
              sentOn: today,
            }
          : null,
    });
  }

  if (petty) {
    if (pettyShort <= 0) {
      done.add('topUp');
    }

    steps.push({
      id: 'topUp',
      title: 'Top up petty cash',
      line:
        pettyShort > 0
          ? `${money(pettyShort)} short of its ${money(petty.floatToKeep ?? 0)} float`
          : `At its ${money(petty.floatToKeep ?? 0)} float`,
      action: 'Top up',
      accountId: petty.id,
      move: pettyShort > 0 ? topUpMove(petty, pettyShort, accounts, today) : null,
    });
  }

  if (accounts.some((a) => a.isActive || a.closing !== 0)) {
    if (dayBookPrinted) {
      done.add('dayBook');
    }

    steps.push({
      id: 'dayBook',
      title: 'Print the day book',
      line: dayBookPrinted ? 'Printed today' : 'One page per account, to sign',
      action: 'Print',
      accountId: null,
      move: null,
    });
  }

  let nextGiven = false;

  return steps.map((step) => {
    if (done.has(step.id)) {
      return { ...step, state: 'done' };
    }

    const state: CloseStepState = nextGiven ? 'later' : 'next';
    nextGiven = true;

    return { ...step, state };
  });
}

/** "1 of 4 done". */
export function closeProgress(steps: readonly CloseStep[]): string {
  return `${steps.filter((step) => step.state === 'done').length} of ${steps.length} done`;
}
